import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2,
  Calendar, 
  ArrowLeft,
  ExternalLink,
  Percent,
  Copy,
  Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { trackEvent } from '../../utils/analytics';
import { useHubProperty, hubPath } from './hubConfig';
import { HubLogo } from './HubBrand';

/**
 * Direct Booking Page
 * 
 * Promotes direct booking with discount and promo code
 * 
 * Route: /hub/book
 */
const withPromo = (url) => `${url}${url.includes('?') ? '&' : '?'}promo=CVS101`;

const BookPage = () => {
  const { slug, propertyId } = useHubProperty();
  const [copied, setCopied] = React.useState(false);
  const promoCode = 'CVS101';
  const discount = '35%';
  const [bookingUrl, setBookingUrl] = React.useState('/properties');

  React.useEffect(() => {
    if (!propertyId) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/properties/${propertyId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => p?.booking_url && setBookingUrl(withPromo(p.booking_url)));
  }, [propertyId]);

  const copyPromoCode = () => {
    navigator.clipboard?.writeText(promoCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    trackEvent('promo_code_copied', { code: promoCode, property_id: propertyId });
  };

  const handleBookClick = () => {
    navigator.clipboard?.writeText(promoCode);
    trackEvent('book_direct_click', { source: 'guest_hub', property_id: propertyId });
  };

  return (
    <div data-testid="hub-book-page" className="min-h-screen bg-slate-900 max-w-lg mx-auto">
      {/* Header */}
      <div className="px-4 pt-6 pb-4">
        <Link to={hubPath(slug)} data-testid="hub-back-link" className="inline-flex items-center text-slate-400 hover:text-white mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Hub
        </Link>
        
        <div className="text-center">
          <div className="mb-6"><HubLogo /></div>
          <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-amber-500">
            <Calendar className="h-8 w-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Book Direct & Save</h1>
          <p className="text-slate-400 text-sm">
            The best rates, guaranteed
          </p>
        </div>
      </div>

      {/* Discount Banner */}
      <div className="px-4 pb-6">
        <Card className="bg-gradient-to-br from-amber-500 to-orange-500 border-0 overflow-hidden">
          <CardContent className="p-6 text-center relative">
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <Percent className="h-10 w-10 text-slate-900 mx-auto mb-2" />
            <h2 className="text-4xl font-bold text-slate-900 mb-1">Save {discount}</h2>
            <p className="text-slate-800 font-medium">
              When you book directly with us
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Promo Code */}
      <div className="px-4 pb-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <p className="text-slate-400 text-xs uppercase tracking-[0.2em] text-center mb-2">Step 1 · Copy your code</p>
            <div data-testid="promo-code-value" className="bg-slate-700 rounded-lg px-4 py-4 text-center mb-3">
              <span className="text-3xl font-mono font-bold text-amber-400 tracking-wider">{promoCode}</span>
            </div>
            <Button onClick={copyPromoCode} data-testid="copy-promo-code-btn"
              className="w-full bg-white hover:bg-slate-100 text-slate-900 font-bold py-6 text-lg">
              {copied ? <><Check className="h-5 w-5 mr-2 text-green-600" />Copied CVS101</> : <><Copy className="h-5 w-5 mr-2" />Copy CVS101</>}
            </Button>
            <p className="text-slate-400 text-xs uppercase tracking-[0.2em] text-center mt-5">Step 2 · Tap Book Now below</p>
            <p className="text-slate-400 text-xs uppercase tracking-[0.2em] text-center mt-2">Step 3 · Paste the code at checkout</p>
          </CardContent>
        </Card>
      </div>

      {/* Why Book Direct */}
      <div className="px-4 pb-6">
        <h3 className="text-white font-semibold mb-3">Why Book Direct?</h3>
        <div className="space-y-2">
          {[
            { text: 'Save up to 35% vs Airbnb/VRBO' },
            { text: 'Flexible cancellation policies' },
            { text: 'Direct communication with hosts' },
            { text: 'Instant booking confirmation' },
            { text: 'Exclusive returning guest perks' }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 text-slate-300 text-sm">
              <CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0" />
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Book Now Button */}
      <div className="px-4 pb-8">
        <a
          href={bookingUrl}
          data-testid="book-direct-btn"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleBookClick}
        >
          <Button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-6 text-lg">
            Book Now
            <ExternalLink className="h-5 w-5 ml-2" />
          </Button>
        </a>
        <p className="text-slate-500 text-xs text-center mt-3">
          You'll be redirected to our secure booking page
        </p>
      </div>

      {/* Properties Preview */}
      <div className="px-4 pb-8">
        <Link to="/properties">
          <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-700">
            Browse All Properties
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default BookPage;
